import React from 'react';
import { useNavigate } from 'react-router-dom';
import { StarIcon, MapPinIcon, CheckCircleIcon, CoffeeIcon, WifiIcon, ShowerHeadIcon, InfoIcon } from 'lucide-react';
import Button from '../common/Button';
import { Hotel } from '../../utils/types';
interface HotelCardProps {
  hotel: Hotel;
  onViewDetails?: (hotel: Hotel) => void;
}
const HotelCard: React.FC<HotelCardProps> = ({
  hotel,
  onViewDetails
}) => {
  const navigate = useNavigate();
  const handleSelect = () => {
    navigate(`/booking/select?hotelId=${hotel.id}`);
  };
  // Generate star rating display
  const renderStars = (count: number) => {
    return Array(5).fill(0).map((_, i) => <StarIcon key={i} className={`h-4 w-4 ${i < count ? 'text-yellow-400' : 'text-gray-300'}`} fill={i < count ? 'currentColor' : 'none'} data-id="element-323" />);
  };
  // Display important amenities
  const renderAmenities = () => {
    const amenityIcons: {
      [key: string]: React.ReactNode;
    } = {
      'Free Wi-Fi': <WifiIcon className="h-4 w-4 text-gray-600" data-id="element-324" />,
      'Breakfast Included': <CoffeeIcon className="h-4 w-4 text-gray-600" data-id="element-325" />,
      'Swimming Pool': <ShowerHeadIcon className="h-4 w-4 text-gray-600" data-id="element-326" />
    };
    return <div className="flex flex-wrap gap-3 mt-3" data-id="element-327">
        {hotel.amenities.slice(0, 3).map(amenity => <div key={amenity} className="flex items-center text-xs text-gray-600" data-id="element-328">
            {amenityIcons[amenity] || null}
            <span className="ml-1" data-id="element-329">{amenity}</span>
          </div>)}
        {hotel.amenities.length > 3 && <div className="text-xs text-gray-600" data-id="element-330">
            +{hotel.amenities.length - 3} more
          </div>}
      </div>;
  };
  return <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow" data-id="element-331">
      <div className="flex flex-col md:flex-row" data-id="element-332">
        <div className="md:w-1/3 h-48 md:h-auto" data-id="element-333">
          <img src={hotel.thumbnail} alt={hotel.name} className="w-full h-full object-cover" data-id="element-334" />
        </div>
        <div className="p-6 md:w-2/3" data-id="element-335">
          <div className="flex flex-col h-full" data-id="element-336">
            <div data-id="element-337">
              <div className="flex justify-between items-start" data-id="element-338">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-339">
                  {hotel.name}
                </h3>
                <div className="flex items-center" data-id="element-340">
                  {hotel.refundable && <div className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs flex items-center mr-2" data-id="element-341">
                      <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-342" />
                      Refundable
                    </div>}
                </div>
              </div>
              <div className="flex items-center mt-1" data-id="element-343">
                <div className="flex mr-2" data-id="element-344">{renderStars(hotel.stars)}</div>
                <span className="text-sm text-gray-500" data-id="element-345">
                  {hotel.stars}-star hotel
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-600 mt-2" data-id="element-346">
                <MapPinIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-347" />
                <span data-id="element-348">
                  {hotel.address}, {hotel.city}, {hotel.country}
                </span>
              </div>
              {renderAmenities()}
              <div className="mt-3 text-sm" data-id="element-349">
                <span className="text-gray-600" data-id="element-350">{hotel.mealPlan}</span>
                <span className="mx-2" data-id="element-351">•</span>
                <span className="text-gray-600" data-id="element-352">
                  {hotel.cancellationPolicy}
                </span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col md:flex-row justify-between md:items-center" data-id="element-353">
              <div className="mb-4 md:mb-0" data-id="element-354">
                <div className="flex items-baseline" data-id="element-355">
                  <span className="text-2xl font-bold text-blue-600" data-id="element-356">
                    ${hotel.pricePerNight}
                  </span>
                  <span className="text-gray-500 text-sm ml-1" data-id="element-357">per night</span>
                </div>
                <span className="text-xs text-gray-500" data-id="element-358">
                  Excludes taxes and fees
                </span>
              </div>
              <div className="flex flex-col" data-id="element-359">
                <div className="flex items-center mb-2" data-id="element-360">
                  <div className="flex mr-1" data-id="element-361">
                    <StarIcon className="h-4 w-4 text-yellow-400" fill="currentColor" data-id="element-362" />
                    <StarIcon className="h-4 w-4 text-yellow-400" fill="currentColor" data-id="element-363" />
                    <StarIcon className="h-4 w-4 text-yellow-400" fill="currentColor" data-id="element-364" />
                    <StarIcon className="h-4 w-4 text-yellow-400" fill="currentColor" data-id="element-365" />
                    <StarIcon className="h-4 w-4 text-gray-300" data-id="element-366" />
                  </div>
                  <span className="text-sm text-gray-600" data-id="element-367">
                    {hotel.rating} ({hotel.reviewCount} reviews)
                  </span>
                </div>
                <div className="flex gap-2" data-id="element-368">
                  {onViewDetails && <Button variant="outline" onClick={() => onViewDetails(hotel)} data-id="element-369">
                      <InfoIcon className="h-4 w-4 mr-1" data-id="element-370" />
                      View Details
                    </Button>}
                  <Button variant="primary" onClick={handleSelect} data-id="element-371">
                    Select Hotel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default HotelCard;